import { useMutation, useQuery, useQueryClient } from "react-query";

import toast from "react-hot-toast";
import { UserService } from "./user.service";

// A failed call arrives in two shapes: `{ ok: false, msg }` for a business rejection (e.g.
// NDT-1680's duplicate car plate) and `{ ok: false, errors: [{ msg }] }` for an
// express-validator rejection. The axios interceptor rejects with that same body, so this
// covers onError too. Reading only one shape leaves the admin with an empty toast.
const toErrorMessage = (payload: any, fallback = "Something went wrong") =>
  payload?.errors?.[0]?.msg ?? payload?.msg ?? fallback;

// GET List
const useGetList = (page: number, option?: Object) => {
  return useQuery(
    ["listUser", page, option],
    async () => {
      const { data } = await UserService.getListUser({
        page,
        ...option,
      });
      return data;
    },
    { keepPreviousData: true }
  );
};

const useGetListRestricted = (page: number) => {
  return useQuery(
    ["listUser", page],
    async () => {
      const { data } = await UserService.getListRestrictedUser({
        page,
      });
      return data;
    },
    { keepPreviousData: true }
  );
};

// CREATE
const useCreate = (cb?: (data: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (createdItem: any) => {
      try {
        const data = await UserService.createUser(createdItem);
        return data;
      } catch (error) {
        return error;
      }
    },
    {
      onSuccess: (data) => {
        cb?.(data);
        if (data.ok) {
          toast.success("Create success! OK!");
          queryClient.invalidateQueries(["listUser"]);
        } else {
          toast.error(toErrorMessage(data));
        }
      },
      onError(error) {
        toast.error(toErrorMessage(error));
      },
    }
  );
};

// UPDATE
const useUpdate = (cb?: (data: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (updatedItem: any) => {
      const data = await UserService.updateUser(updatedItem);
      return data;
    },
    {
      onSuccess: (data) => {
        cb?.(data);
        if (data.ok) {
          toast.success("Update success! OK!");
          queryClient.invalidateQueries(["userDetail", data.data._id]);
          queryClient.invalidateQueries(["listUser"]);
        } else {
          toast.error(toErrorMessage(data));
        }
      },
      onError(error) {
        toast.error(toErrorMessage(error));
      },
    }
  );
};

// NDT-1511: dedicated hook rather than reusing useUpdate so the toast names the feature the
// admin actually toggled. Invalidates the detail query as well as the list, because the
// switch lives on the user detail page.
const useTogglePlugNCharge = (cb?: (data: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ userId, enabled }: { userId: string; enabled: boolean }) => {
      const data = await UserService.updateUser({
        _id: userId,
        plug_n_charge_enabled: enabled,
      });
      return data;
    },
    {
      onSuccess: (data, variables) => {
        cb?.(data);
        if (data.ok) {
          toast.success(
            variables.enabled
              ? "Plug & Charge enabled!"
              : "Plug & Charge disabled!"
          );
          queryClient.invalidateQueries(["userDetail", variables.userId]);
          queryClient.invalidateQueries(["listUser"]);
        } else {
          // NDT-1680: this is where the "plate already enabled on another user" rejection
          // surfaces, so it must not fall back to an empty toast.
          toast.error(toErrorMessage(data));
        }
      },
      onError(error: any) {
        toast.error(toErrorMessage(error));
        return error;
      },
    }
  );
};

const useListAdmin = (params: TParams & { role_names?: string[] }) => {
  return useQuery(
    ["list_cpo_sp", params],
    async () => {
      const { data } = await UserService.getListAdmins(params);
      return data;
    },
    { keepPreviousData: true }
  );
};

const useUserDetail = (id: string) => {
  return useQuery(
    ["userDetail", id],
    async () => {
      const data = await UserService.getUserDetail(id);
      return data;
    },
    { enabled: !!id }
  );
};

const useTopUpByUser = (callback?: (data: any) => void) => {
  return useMutation(
    async (updatedItem: any) => {
      const dataTopUp = await UserService.topUpByUser(updatedItem);
      return dataTopUp;
    },
    {
      onSuccess: (data) => {
        callback?.(data);
        if (data.ok) {
          toast.success("Top up success!");
        } else {
          toast.error(data?.msg);
        }
      },
      onError(error) {
        toast.error(error as string);
      },
    }
  );
};

const useRefundByTransaction = (callback?: (data: any) => void) => {
  return useMutation(
    async (updatedItem: any) => {
      const dataRefund = await UserService.refundByTransaction(updatedItem);
      return dataRefund;
    },
    {
      onSuccess: (data) => {
        callback?.(data);
        if (data.ok) {
          toast.success("Refund success!");
        } else {
          toast.error(data?.msg);
        }
      },
      onError(error: any) {
        toast.error(error?.msg);
        return error;
      },
    }
  );
};

const useDeleteUser = (callback?: (data: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (userId: string) => {
      const dataDelete = await UserService.deleteUser(userId);
      return dataDelete;
    },
    {
      onSuccess: (data) => {
        callback?.(data);
        if (data.ok) {
          toast.success("Delete success!");
          queryClient.invalidateQueries(["listUser"]);
        } else {
          toast.error(data?.msg);
        }
      },
      onError(error: any) {
        toast.error(error?.msg);
        return error;
      },
    }
  );
};

const useRestoreUser = (callback?: (data: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (userId: string) => {
      const dataRestore = await UserService.restoreUser(userId);
      return dataRestore;
    },
    {
      onSuccess: (data) => {
        callback?.(data);
        if (data.ok) {
          toast.success("Restore success!");
          queryClient.invalidateQueries(["listUser"]);
        } else {
          toast.error(data?.msg);
        }
      },
      onError: (error: any) => {
        toast.error(error?.msg);
        return error;
      },
    }
  );
};
export {
  useCreate,
  useDeleteUser,
  useGetList,
  useGetListRestricted,
  useListAdmin,
  useRefundByTransaction,
  useRestoreUser,
  useTogglePlugNCharge,
  useTopUpByUser,
  useUpdate,
  useUserDetail,
};
